import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { localePath } from "@/i18n/localePath";
import { ScrollReveal } from "./ScrollEffects";
import { Tilt3DCard } from "./Tilt3DCard";
import { Button } from "@/components/ui/button";
import { ModeSplashTransition } from "./ModeSplashTransition";
import heroDog from "@/assets/hero-dog.png";
import dogChat from "@/assets/dog-chat.png";

/* ─── Mode card ─── */
interface ModeCardProps {
  index: number;
  icon: string;
  image?: string;
  imageElement?: React.ReactNode;
  title: string;
  subSlogan: string;
  description: string;
  buttonText: string;
  onButtonClick: (e: React.MouseEvent) => void;
  variant: "everyday" | "memorial" | "kids";
  accentGradient: string;
}

const ModeCard: React.FC<ModeCardProps> = ({
  index, icon, image, imageElement, title, subSlogan, description,
  buttonText, onButtonClick, variant, accentGradient,
}) => {
  const styles = {
    everyday: {
      card: "bg-gradient-to-br from-[hsla(25,80%,95%,0.5)] to-[hsla(30,60%,97%,0.3)] border-primary/15",
      button: "w-full rounded-xl h-11 font-bold shadow-warm-sm",
      buttonVariant: "default" as const,
      hoverShadow: "0 12px 32px -8px hsl(20 100% 60% / 0.15)",
    },
    memorial: {
      card: "bg-gradient-to-br from-[hsla(235,40%,96%,0.6)] to-[hsla(240,30%,97%,0.3)] border-[hsla(230,40%,75%,0.2)]",
      button: "w-full rounded-xl h-11 font-bold border-[hsla(230,40%,75%,0.3)] text-[hsl(230,35%,45%)] hover:bg-[hsla(230,40%,92%,0.5)]",
      buttonVariant: "outline" as const,
      hoverShadow: "0 12px 32px -8px hsl(230 40% 60% / 0.12)",
    },
    kids: {
      card: "bg-gradient-to-br from-[hsla(48,90%,95%,0.6)] to-[hsla(80,50%,95%,0.4)] border-2 border-[hsla(45,90%,60%,0.35)]",
      button: "w-full rounded-xl h-12 font-extrabold text-base shadow-lg bg-gradient-to-r from-[hsl(45,90%,55%)] to-[hsl(75,60%,48%)] hover:from-[hsl(45,90%,50%)] hover:to-[hsl(75,60%,43%)] text-white border-0",
      buttonVariant: "default" as const,
      hoverShadow: "0 12px 32px -8px hsl(45 90% 55% / 0.2)",
    },
  };

  const s = styles[variant];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <Tilt3DCard className="rounded-3xl h-full">
        <motion.div
          className={`glass rounded-3xl shadow-warm-sm h-full flex flex-col border overflow-hidden ${s.card}`}
          whileHover={{ y: -4, boxShadow: s.hoverShadow }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <div className="h-1" style={{ background: accentGradient }} />
          <div className="p-7 flex flex-col flex-1">
            <div className="flex items-center gap-3.5 mb-2">
              {image ? (
                <motion.img
                  src={image}
                  alt=""
                  className="w-24 h-24 sm:w-28 sm:h-28 object-contain"
                  whileHover={{ scale: 1.1, rotate: variant === "memorial" ? -5 : 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
              ) : imageElement}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{icon}</span>
                  <h3 className="font-bold text-lg text-foreground">{title}</h3>
                </div>
              </div>
            </div>
            <p className="text-xs italic text-muted-foreground/70 mb-4 pl-1" style={{ color: "hsl(30 30% 45%)" }}>
              {subSlogan}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
              {description}
            </p>
            <Button onClick={onButtonClick} variant={s.buttonVariant} className={s.button}>
              {buttonText}
            </Button>
          </div>
        </motion.div>
      </Tilt3DCard>
    </motion.div>
  );
};

export const ModesSection: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lp = (path: string) => localePath(path, i18n.language);
  const [splash, setSplash] = useState<{ mode: "everyday" | "memorial" | "kids"; rect: DOMRect | null } | null>(null);

  const handleModeClick = useCallback((mode: "everyday" | "memorial" | "kids", e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setSplash({ mode, rect });
  }, []);

  const handleSplashComplete = useCallback(() => {
    if (!splash) return;
    navigate(lp("/onboarding"), { state: { mode: splash.mode } });
  }, [splash, navigate]);

  return (
    <>
      <ModeSplashTransition
        active={!!splash}
        mode={splash?.mode ?? "everyday"}
        originRect={splash?.rect ?? null}
        onComplete={handleSplashComplete}
      />
      <section className="py-16 sm:py-20 px-4 relative" id="modes">
        <div className="absolute inset-0 pointer-events-none" />
        <div className="max-w-6xl mx-auto relative z-10">
          <ScrollReveal className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-4">
              {t('modes.section_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-2">
              {t('modes.section_title')}
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-4 md:gap-5 items-stretch">
            <ModeCard
              index={0}
              icon="☀️"
              image={heroDog}
              title={t('modes.living.title')}
              subSlogan={t('modes.living.sub_slogan')}
              description={t('modes.living.description')}
              buttonText={t('modes.living.button')}
              onButtonClick={(e) => handleModeClick("everyday", e)}
              variant="everyday"
              accentGradient="linear-gradient(90deg, hsl(20 100% 60%), hsl(35 80% 65%))"
            />
            <ModeCard
              index={1}
              icon="🌈"
              image={dogChat}
              title={t('modes.memorial.title')}
              subSlogan={t('modes.memorial.sub_slogan')}
              description={t('modes.memorial.description')}
              buttonText={t('modes.memorial.button')}
              onButtonClick={(e) => handleModeClick("memorial", e)}
              variant="memorial"
              accentGradient="linear-gradient(90deg, hsl(230 40% 70%), hsl(160 30% 65%))"
            />
            <ModeCard
              index={2}
              icon="🐕"
              imageElement={
                <motion.div
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-[hsl(45,90%,65%)] to-[hsl(80,60%,55%)] flex items-center justify-center text-5xl shadow-md"
                  whileHover={{ scale: 1.1, rotate: 8 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  🐕
                </motion.div>
              }
              title={t('modes.kids.title')}
              subSlogan={t('modes.kids.sub_slogan')}
              description={t('modes.kids.description')}
              buttonText={t('modes.kids.button')}
              onButtonClick={(e) => handleModeClick("kids", e)}
              variant="kids"
              accentGradient="linear-gradient(90deg, hsl(45 90% 55%), hsl(80 60% 48%))"
            />
          </div>
        </div>
      </section>
    </>
  );
};
